import { Card, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { tokens } from "app/theme";
import { Team } from "resource-planner-model";
import { Link } from "react-router-dom";
import { useCustomerBudgetOutOfSynchNotifications } from "./customer/useCustomerBudgetOutOfSynchNotifications";
import { useCustomerOverBudgetNotifications } from "./customer/useCustomerOverBudgetNotifications";
import { useCustomersOffboardedNotifications } from "./customer/useCustomersOffboardedNotifications";
import { useCustomersOnboardedNotifications } from "./customer/useCustomersOnboardedNotifications";
import { useCustomersWithNoEMNotifications } from "./customer/useCustomerWithNoEMNotifications";
import { useCustomersWithNoTeamNotifications } from "./customer/useCustomerWithNoTeamNotifications";
import { useEngineersOffboardedNotifications } from "./engineer/useEngineersOffboardedNotifications";
import { useEngineersOnboardedNotifications } from "./engineer/useEngineersOnboardedNotifications";
import useSynchNotifications from "./synch/useTeamNotifications";
import { SystemNotification } from "./SystemNotificationTypes";
import { useOverallocatedTeamNotifications } from "./team/useOverallocatedTeamNotifications";

interface Severity {
  dot: string;
  text: string;
  bg: string;
  bd: string;
  glow: string;
}

const SEVERITIES: { [key: string]: Severity } = {
  crit: {
    dot: "hsl(0, 72%, 58%)",
    text: "hsl(0, 72%, 76%)",
    bg: "hsla(0, 72%, 58%, 0.12)",
    bd: "hsla(0, 72%, 58%, 0.34)",
    glow: "0 0 8px hsla(0, 72%, 58%, 0.55)",
  },
  warn: {
    dot: "hsl(38, 92%, 56%)",
    text: "hsl(38, 92%, 70%)",
    bg: "hsla(38, 92%, 56%, 0.12)",
    bd: "hsla(38, 92%, 56%, 0.34)",
    glow: "0 0 8px hsla(38, 92%, 56%, 0.45)",
  },
  info: {
    dot: "hsl(199, 84%, 56%)",
    text: "hsl(199, 84%, 70%)",
    bg: "hsla(199, 84%, 56%, 0.12)",
    bd: "hsla(199, 84%, 56%, 0.34)",
    glow: "none",
  },
};

interface WorklistItem {
  severity: string;
  pillLabel: string;
  notification: SystemNotification;
}

function toItems(
  notifications: SystemNotification[],
  severity: string,
  pillLabel: string
): WorklistItem[] {
  return notifications.map((notification) => ({
    severity,
    pillLabel,
    notification,
  }));
}

function WorklistRow({ item }: { item: WorklistItem }) {
  const sev = SEVERITIES[item.severity];
  const row = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "16px 1fr auto",
        gap: 12,
        padding: "11px 14px",
        borderBottom: `1px solid ${tokens.hairline}`,
        alignItems: "center",
        cursor: item.notification.link ? "pointer" : "default",
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flex: "none",
          background: sev.dot,
          boxShadow: sev.glow,
        }}
      />
      <div>
        <div style={{ font: `500 13px/1.3 ${tokens.fontSans}`, color: tokens.fg1 }}>
          {item.notification.title}
        </div>
        <div
          style={{
            font: `400 12px/1.4 ${tokens.fontMono}`,
            color: tokens.fg3,
            marginTop: 3,
          }}
        >
          {item.notification.description}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 22,
            padding: "0 9px",
            borderRadius: 999,
            font: `500 11px/1 ${tokens.fontSans}`,
            whiteSpace: "nowrap",
            background: sev.bg,
            border: `1px solid ${sev.bd}`,
            color: sev.text,
          }}
        >
          {item.pillLabel}
        </span>
        <ChevronRightIcon style={{ fontSize: 15, color: tokens.fg3 }} />
      </div>
    </div>
  );
  return item.notification.link ? (
    <Link
      to={item.notification.link}
      style={{ textDecoration: "none", color: "inherit", display: "block" }}
    >
      {row}
    </Link>
  ) : (
    row
  );
}

export function NeedsAttentionCard({
  year,
  month,
  teams,
}: {
  year: number;
  month: number;
  teams: { [teamId: string]: Team };
}) {
  const synchErrors = useSynchNotifications();
  const noTeam = useCustomersWithNoTeamNotifications(year, month);
  const noEm = useCustomersWithNoEMNotifications(year, month);
  const budgetOutOfSynch = useCustomerBudgetOutOfSynchNotifications(year, month);
  const overallocated = useOverallocatedTeamNotifications(year, month, teams);
  const overBudget = useCustomerOverBudgetNotifications(year, month);
  const customersOnboarded = useCustomersOnboardedNotifications(year, month);
  const customersOffboarded = useCustomersOffboardedNotifications(year, month);
  const engineersOnboarded = useEngineersOnboardedNotifications(year, month);
  const engineersOffboarded = useEngineersOffboardedNotifications(year, month);

  const items: WorklistItem[] = [
    ...toItems(synchErrors, "crit", "Sync error"),
    ...toItems(noTeam, "crit", "No team"),
    ...toItems(noEm, "crit", "No PM"),
    ...toItems(budgetOutOfSynch, "crit", "Out of sync"),
    ...toItems(overallocated, "warn", "Over"),
    ...toItems(overBudget, "warn", "Over budget"),
    ...toItems(customersOnboarded, "info", "Onboarded"),
    ...toItems(customersOffboarded, "info", "Offboarded"),
    ...toItems(engineersOnboarded, "info", "Onboarded"),
    ...toItems(engineersOffboarded, "info", "Offboarded"),
  ];
  const openCount = items.filter((i) => i.severity !== "info").length;

  return (
    <Card style={{ overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          borderBottom: `1px solid ${tokens.hairline}`,
        }}
      >
        <Typography variant="h5" style={{ margin: 0, fontSize: 13 }}>
          Needs attention
        </Typography>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 22,
            padding: "0 8px",
            borderRadius: 999,
            font: `500 11px/1 ${tokens.fontSans}`,
            background:
              openCount > 0 ? SEVERITIES.crit.bg : SEVERITIES.info.bg,
            border: `1px solid ${
              openCount > 0 ? SEVERITIES.crit.bd : SEVERITIES.info.bd
            }`,
            color: openCount > 0 ? SEVERITIES.crit.text : SEVERITIES.info.text,
          }}
        >
          {openCount} open
        </span>
        <span
          style={{
            font: `400 11px/1 ${tokens.fontMono}`,
            color: tokens.fg3,
            marginLeft: "auto",
          }}
        >
          sorted by severity
        </span>
      </div>
      {items.length > 0 ? (
        items.map((item) => (
          <WorklistRow
            key={`${item.pillLabel}-${item.notification.source}-${item.notification.id}`}
            item={item}
          />
        ))
      ) : (
        <div
          style={{
            padding: "18px 14px",
            font: `400 12px/1.4 ${tokens.fontMono}`,
            color: tokens.fg3,
          }}
        >
          Nothing needs attention for this period.
        </div>
      )}
    </Card>
  );
}
